<template>
  <div class="portfolio-instructions-editable">
    <PortfolioInstructionsDisplay
      v-if="!isEditing"
      :portfolio="props.portfolio"
      :registryDevices="props.registryDevices"
    >
      <template #edit-button>
        <button
          class="btn btn-sm btn-outline-primary"
          @click="startEditing"
          :disabled="props.isGlobalEditingActive && !isEditing"
        >
          <FontAwesomeIcon icon="fas fa-pencil-alt" class="me-1" />
          {{ $t("common.edit") }}
        </button>
      </template>
    </PortfolioInstructionsDisplay>

    <div v-else class="portfolio-instructions-editor p-3 border rounded mb-3">
      <form @submit.prevent="saveEdits">
        <PPAInstructionsEditor
          v-if="props.portfolio.portfolio_type === 'PPA'"
          v-model="localPortfolio"
          :usesCnmcIssuance="props.usesCnmcIssuance ?? false"
          :registryDevices="props.registryDevices"
          :existingPortfolios="props.existingPortfolios"
          @error:ai-merge="globalErrorHandler"
        />
        <SelfManagedInstructionsEditor
          v-else-if="props.portfolio.portfolio_type === 'HOLD'"
          v-model="localPortfolio"
          :registryDevices="props.registryDevices"
        />

        <div class="d-flex justify-content-end mt-3">
          <button
            type="button"
            class="btn btn-secondary me-2"
            @click="cancelEditing"
          >
            {{ $t("common.cancel") }}
          </button>
          <button type="submit" class="btn btn-primary">
            {{ $t("common.save") }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { PortfolioWithInstructions, RegistryDevice } from "@/types";
import useVuelidate from "@vuelidate/core";
import { cloneDeep } from "lodash-es";
import { computed, ref, watch } from "vue";
import PortfolioInstructionsDisplay from "./PortfolioInstructionsDisplay.vue";
import PPAInstructionsEditor from "./PPA/PPAInstructionsEditor.vue";
import SelfManagedInstructionsEditor from "./self-managed/SelfManagedInstructionsEditor.vue";

const { globalErrorHandler } = useGlobalHandlers();

const props = defineProps<{
  portfolio: PortfolioWithInstructions;
  registryDevices: RegistryDevice[];
  // Other PPAs of the company; the PPA editor judges device conflicts against them
  existingPortfolios?: PortfolioWithInstructions[];
  usesCnmcIssuance?: boolean;
  isGlobalEditingActive?: boolean;
  startInEditMode?: boolean;
}>();

const emit = defineEmits<{
  save: [portfolio: PortfolioWithInstructions];
  cancel: [];
  edit: [];
  editingStateChange: [isEditing: boolean];
}>();

const isEditing = ref(props.startInEditMode || false);
const localPortfolio = ref<PortfolioWithInstructions>(
  cloneDeep(props.portfolio)
);

// Initialize Vuelidate to be aware of localPortfolio.
// This setup allows Vuelidate to discover and aggregate validation states
// from child components that validate parts of localPortfolio (via v-model).
const v$ = useVuelidate(
  computed(() => ({})),
  localPortfolio
);

watch(
  () => props.portfolio,
  (newVal) => {
    if (!isEditing.value) {
      localPortfolio.value = cloneDeep(newVal);
    }
  },
  { deep: true }
);

// Initialize editing state on mount if startInEditMode is true
watch(
  () => props.startInEditMode,
  (newVal) => {
    if (newVal && !isEditing.value) {
      startEditing();
    }
  },
  { immediate: true }
);

const startEditing = () => {
  localPortfolio.value = cloneDeep(props.portfolio);
  isEditing.value = true;
  emit("edit");
  emit("editingStateChange", true);
};

const saveEdits = async () => {
  v$.value.$touch(); // This will trigger validation in children validating parts of localPortfolio
  if (v$.value.$invalid) {
    return;
  }
  emit("save", localPortfolio.value);
  isEditing.value = false;
  emit("editingStateChange", false);
};

const cancelEditing = () => {
  localPortfolio.value = cloneDeep(props.portfolio);
  isEditing.value = false;
  emit("cancel");
  emit("editingStateChange", false);
};
</script>

<style scoped lang="scss">
.portfolio-instructions-editor {
  background-color: var(--bs-gray-100);
}
</style>
